import apiClient from './client'
import type { PaginatedResponse } from '@/types'

// ============ COMPLIANCE CLINIC ============

export interface ClinicCategory {
  id: number
  name: string
  slug: string
  description: string
  icon: string
  questions_count: number
}

export interface ClinicQuestion {
  id: number
  category: ClinicCategory
  author: {
    id: number
    full_name: string
    avatar: string | null
  }
  title: string
  content: string
  status: 'open' | 'answered' | 'closed'
  views_count: number
  answers_count: number
  is_anonymous: boolean
  is_experts_only: boolean
  created_at: string
  updated_at: string
}

export interface ClinicAnswer {
  id: number
  question_id: number
  expert: {
    id: number
    full_name: string
    avatar: string | null
    role: string
  }
  content: string
  is_official: boolean
  helpful_count: number
  has_voted_helpful?: boolean
  created_at: string
}

// ============ EXPERTISE NETWORK ============

export interface ExpertiseCategory {
  id: number
  name: string
  slug: string
  description: string
  cases_count: number
}

export interface ExpertiseCase {
  id: number
  category: ExpertiseCategory
  author: {
    id: number
    full_name: string
    avatar: string | null
  }
  title: string
  context: string
  problem: string
  question: string
  status: 'open' | 'in_discussion' | 'resolved' | 'closed'
  responses_count: number
  is_featured: boolean
  attachments: {
    id: number
    name: string
    url: string
  }[]
  created_at: string
  updated_at: string
}

export interface ExpertiseResponse {
  id: number
  case_id: number
  expert: {
    id: number
    full_name: string
    avatar: string | null
    role: string
  }
  content: string
  is_best_answer: boolean
  comments_count: number
  created_at: string
}

export const servicesApi = {
  // ============ COMPLIANCE CLINIC ============

  // Categories
  getClinicCategories: async (): Promise<ClinicCategory[]> => {
    const response = await apiClient.get('/services/clinic/categories/')
    // Handle both array and paginated response formats
    if (Array.isArray(response.data)) {
      return response.data
    }
    return response.data.results || []
  },

  // Questions
  getClinicQuestions: async (params?: {
    category?: string
    status?: string
    search?: string
    page?: number
  }): Promise<PaginatedResponse<ClinicQuestion>> => {
    const response = await apiClient.get('/services/clinic/questions/', { params })
    return response.data
  },

  getClinicQuestion: async (id: number): Promise<ClinicQuestion & { answers: ClinicAnswer[] }> => {
    const response = await apiClient.get(`/services/clinic/questions/${id}/`)
    return response.data
  },

  createClinicQuestion: async (data: {
    category_id: number
    title: string
    content: string
    is_anonymous?: boolean
    is_experts_only?: boolean
  }): Promise<ClinicQuestion> => {
    // Map frontend field names to backend field names
    const payload = {
      category: data.category_id,
      title: data.title,
      question: data.content,
      is_anonymous: data.is_anonymous || false,
      is_experts_only: data.is_experts_only || false,
    }
    const response = await apiClient.post('/services/clinic/questions/', payload)
    return response.data
  },

  closeClinicQuestion: async (id: number): Promise<void> => {
    await apiClient.post(`/services/clinic/questions/${id}/close/`)
  },

  // Answers
  createClinicAnswer: async (questionId: number, content: string): Promise<ClinicAnswer> => {
    const response = await apiClient.post(`/services/clinic/questions/${questionId}/answer/`, {
      answer: content,
    })
    return response.data
  },

  markAnswerHelpful: async (answerId: number): Promise<void> => {
    await apiClient.post(`/services/clinic/answers/${answerId}/helpful/`)
  },

  unmarkAnswerHelpful: async (answerId: number): Promise<void> => {
    await apiClient.delete(`/services/clinic/answers/${answerId}/helpful/`)
  },

  // ============ EXPERTISE NETWORK ============

  // Categories
  getExpertiseCategories: async (): Promise<ExpertiseCategory[]> => {
    const response = await apiClient.get('/services/expertise/categories/')
    return response.data
  },

  // Cases
  getExpertiseCases: async (params?: {
    category?: string
    status?: string
    search?: string
    page?: number
  }): Promise<PaginatedResponse<ExpertiseCase>> => {
    const response = await apiClient.get('/services/expertise/cases/', { params })
    return response.data
  },

  getExpertiseCase: async (id: number): Promise<ExpertiseCase & { responses: ExpertiseResponse[] }> => {
    const response = await apiClient.get(`/services/expertise/cases/${id}/`)
    return response.data
  },

  createExpertiseCase: async (data: FormData): Promise<ExpertiseCase> => {
    const response = await apiClient.post('/services/expertise/cases/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  updateCaseStatus: async (id: number, status: ExpertiseCase['status']): Promise<void> => {
    await apiClient.patch(`/services/expertise/cases/${id}/`, { status })
  },

  // Responses
  createExpertiseResponse: async (caseId: number, content: string): Promise<ExpertiseResponse> => {
    const response = await apiClient.post(`/services/expertise/cases/${caseId}/responses/`, {
      content,
    })
    return response.data
  },

  markBestAnswer: async (responseId: number): Promise<void> => {
    await apiClient.post(`/services/expertise/responses/${responseId}/best/`)
  },

  // Comments on responses
  addResponseComment: async (responseId: number, content: string): Promise<void> => {
    await apiClient.post(`/services/expertise/responses/${responseId}/comments/`, {
      content,
    })
  },

  // Featured cases
  getFeaturedCases: async (limit?: number): Promise<ExpertiseCase[]> => {
    const response = await apiClient.get('/services/expertise/featured/', {
      params: { limit },
    })
    return response.data
  },

  // ============ EXPERT MANAGEMENT (Admin) ============

  // Auto-assign a single question
  autoAssignQuestion: async (questionId: number): Promise<{ detail: string; question: ClinicQuestion }> => {
    const response = await apiClient.post(`/services/clinic/questions/${questionId}/auto_assign/`)
    return response.data
  },

  // Bulk auto-assign all pending questions
  bulkAutoAssign: async (): Promise<{
    detail: string
    results: {
      clinic_assigned: number
      clinic_failed: number
      expertise_assigned: number
      expertise_failed: number
      messages: string[]
    }
  }> => {
    const response = await apiClient.post('/services/clinic/questions/bulk_auto_assign/')
    return response.data
  },

  // Get pending count by category
  getPendingCount: async (): Promise<{
    category_id: number
    category_name: string
    pending_count: number
    available_experts: number
  }[]> => {
    const response = await apiClient.get('/services/clinic/questions/pending_count/')
    return response.data
  },

  // Get expert specializations
  getExpertSpecializations: async (params?: {
    clinic_category?: number
    expertise_category?: number
    is_available?: boolean
  }): Promise<ExpertSpecialization[]> => {
    const response = await apiClient.get('/services/admin/expert-specializations/', { params })
    return response.data.results || response.data
  },

  // Create expert specialization
  createExpertSpecialization: async (data: {
    expert: number
    clinic_category?: number
    expertise_category?: number
    expertise_level: string
    max_assignments: number
    is_available: boolean
    priority: number
  }): Promise<ExpertSpecialization> => {
    const response = await apiClient.post('/services/admin/expert-specializations/', data)
    return response.data
  },

  // Update expert specialization
  updateExpertSpecialization: async (id: number, data: Partial<{
    expertise_level: string
    max_assignments: number
    is_available: boolean
    priority: number
  }>): Promise<ExpertSpecialization> => {
    const response = await apiClient.patch(`/services/admin/expert-specializations/${id}/`, data)
    return response.data
  },

  // Delete expert specialization
  deleteExpertSpecialization: async (id: number): Promise<void> => {
    await apiClient.delete(`/services/admin/expert-specializations/${id}/`)
  },

  // Get available experts (users who can be assigned)
  getAvailableExperts: async (): Promise<{
    id: number
    username: string
    email: string
    full_name: string
    role: string
    job_title: string
  }[]> => {
    const response = await apiClient.get('/services/admin/expert-specializations/available_experts/')
    return response.data
  },

  // Get workload stats
  getWorkloadStats: async (): Promise<{
    id: number
    expert_id: number
    expert_name: string
    category: string
    category_type: 'clinic' | 'expertise'
    level: string
    current: number
    max: number
    available: boolean
    at_capacity: boolean
  }[]> => {
    const response = await apiClient.get('/services/admin/expert-specializations/workload_stats/')
    return response.data
  },

  // Get clinic categories with routing stats
  getClinicCategoriesRouting: async (): Promise<{
    id: number
    name: string
    slug: string
    questions_count: number
    pending_count: number
    experts_count: number
    available_experts_count: number
  }[]> => {
    const response = await apiClient.get('/services/admin/clinic-categories-routing/')
    return response.data.results || response.data
  },

  // Get experts for a category
  getCategoryExperts: async (categorySlug: string): Promise<{
    expert_id: number
    expert_name: string
    expert_email: string
    level: string
    level_display: string
    current_assignments: number
    max_assignments: number
    is_available: boolean
    is_at_capacity: boolean
    priority: number
  }[]> => {
    const response = await apiClient.get(`/services/admin/clinic-categories-routing/${categorySlug}/experts/`)
    return response.data
  },

  // Auto-assign pending in category
  autoAssignCategoryPending: async (categorySlug: string): Promise<{
    assigned: number
    failed: number
    messages: string[]
  }> => {
    const response = await apiClient.post(`/services/admin/clinic-categories-routing/${categorySlug}/auto_assign_pending/`)
    return response.data
  },

  // Get assignment logs
  getAssignmentLogs: async (params?: {
    assignment_type?: 'clinic' | 'expertise'
    assignment_method?: 'auto' | 'manual'
    page?: number
  }): Promise<PaginatedResponse<AssignmentLog>> => {
    const response = await apiClient.get('/services/admin/assignment-logs/', { params })
    return response.data
  },

  // Get assignment stats
  getAssignmentStats: async (): Promise<{
    total_assignments: number
    auto_assignments: number
    manual_assignments: number
    clinic_assignments: number
    expertise_assignments: number
    avg_response_time_hours: number | null
  }> => {
    const response = await apiClient.get('/services/admin/assignment-logs/stats/')
    return response.data
  },
}

// Types for expert management
export interface ExpertSpecialization {
  id: number
  expert: number
  expert_name: string
  expert_email: string
  clinic_category: number | null
  clinic_category_name: string | null
  expertise_category: number | null
  expertise_category_name: string | null
  expertise_level: 'junior' | 'mid' | 'senior' | 'lead'
  max_assignments: number
  is_available: boolean
  priority: number
  current_assignments: number
  is_at_capacity: boolean
  created_at: string
  updated_at: string
}

export interface AssignmentLog {
  id: number
  assignment_type: 'clinic' | 'expertise'
  assignment_method: 'auto' | 'manual'
  question: number | null
  question_title: string | null
  case: number | null
  case_title: string | null
  assigned_expert: number
  assigned_expert_name: string
  assigned_by: number | null
  assigned_by_name: string
  category_name: string
  reason: string
  response_time_hours: number | null
  created_at: string
}

// ============ SERVICES CATALOG TYPES ============

export interface ServiceCategory {
  id: number
  name: string
  slug: string
  description: string
  icon: string
  order: number
  is_active: boolean
  services_count: number
}

export interface Service {
  id: number
  name: string
  slug: string
  short_description: string
  description?: string
  icon: string
  category: number | ServiceCategory
  category_name?: string
  category_slug?: string
  is_free: boolean
  is_active: boolean
  order: number
  external_url?: string
  provider?: string
  created_at?: string
  updated_at?: string
}

// ============ SERVICES CATALOG API ============

export const servicesApiCatalog = {
  // Get service categories
  getServiceCategories: async (): Promise<ServiceCategory[]> => {
    const response = await apiClient.get('/services/services/categories/')
    if (Array.isArray(response.data)) {
      return response.data
    }
    return response.data.results || []
  },

  // Get services list
  getServices: async (params?: {
    category_slug?: string
    is_free?: boolean
    search?: string
  }): Promise<Service[]> => {
    const response = await apiClient.get('/services/services/list/', { params })
    if (Array.isArray(response.data)) {
      return response.data
    }
    return response.data.results || []
  },

  // Get single service by slug
  getService: async (slug: string): Promise<Service> => {
    const response = await apiClient.get(`/services/services/list/${slug}/`)
    return response.data
  },
}

// ============ SERVICES ADMIN API ============

export const servicesAdminApi = {
  // === Service Categories Admin ===
  getServiceCategories: async (): Promise<ServiceCategory[]> => {
    const response = await apiClient.get('/services/admin/service-categories/')
    if (Array.isArray(response.data)) {
      return response.data
    }
    return response.data.results || []
  },

  createServiceCategory: async (data: {
    name: string
    description?: string
    icon?: string
    order?: number
    is_active?: boolean
  }): Promise<ServiceCategory> => {
    const response = await apiClient.post('/services/admin/service-categories/', data)
    return response.data
  },

  updateServiceCategory: async (id: number, data: Partial<{
    name: string
    description: string
    icon: string
    order: number
    is_active: boolean
  }>): Promise<ServiceCategory> => {
    const response = await apiClient.patch(`/services/admin/service-categories/${id}/`, data)
    return response.data
  },

  deleteServiceCategory: async (id: number): Promise<void> => {
    await apiClient.delete(`/services/admin/service-categories/${id}/`)
  },

  // === Services Admin ===
  getServices: async (): Promise<Service[]> => {
    const response = await apiClient.get('/services/admin/services/')
    if (Array.isArray(response.data)) {
      return response.data
    }
    return response.data.results || []
  },

  createService: async (data: {
    name: string
    category: number
    short_description?: string
    description?: string
    icon?: string
    is_free?: boolean
    is_active?: boolean
    order?: number
    external_url?: string
    provider?: string
  }): Promise<Service> => {
    const response = await apiClient.post('/services/admin/services/', data)
    return response.data
  },

  updateService: async (id: number, data: Partial<{
    name: string
    category: number
    short_description: string
    description: string
    icon: string
    is_free: boolean
    is_active: boolean
    order: number
    external_url: string
    provider: string
  }>): Promise<Service> => {
    const response = await apiClient.patch(`/services/admin/services/${id}/`, data)
    return response.data
  },

  deleteService: async (id: number): Promise<void> => {
    await apiClient.delete(`/services/admin/services/${id}/`)
  },
}
